import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { ApplicationSettings } from 'projects/lib_config/src/public-api';

import { Company } from '../models/master-data/company.model';
import { CompanyListItem } from '../models/master-data/company-list-item.model';
import { LegalEntity } from '../models/master-data/legal-entity.model';
import { JurisdictionObligation } from '../models/master-data/jurisdiction-obligation.model';
import { CompanySimpleListItem } from '../models/master-data/company-simple-list-item.model';
import { PagedResult } from '../models/paged-result.model';
import { LegalEntitySimpleListItem } from '../models/master-data/legal-entity-simple-list-item.model';
import { JurisdictionObligationSimpleListItem } from '../models/master-data/jurisdiction-obligation-simple-list-item.model';
import { JurisdictionObligationOption } from '../../public-api';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MasterDataService {

  readonly disabledJurisdictions = ['DE'];
  
  constructor(private http: HttpClient, private settings: ApplicationSettings) {

  }

  getCompanyList(filter: string, showInactive: boolean, page: number = 1, pageSize: number = 10): Observable<PagedResult<CompanyListItem>> {
    let params: HttpParams = new HttpParams();
    if (filter) params = params.set('SearchQuery', filter);
    if (showInactive) params = params.set('ShowInactive', showInactive.toString());
    params = params.set('PageNumber', page.toString());
    params = params.set('PageSize', pageSize.toString());

    return this.http.get<PagedResult<CompanyListItem>>(this.settings.backendUrlMasterData + `/company/all`, { params: params });
  }

  getSimpleCompanyList(filter: string) {
    let params: HttpParams = new HttpParams();
    if (filter) params = params.set('SearchQuery', filter);
    return this.http.get<CompanySimpleListItem[]>(this.settings.backendUrlMasterData + `/company/all/list`, { params: params });
  }

  getCompany(id: string): Observable<Company> {
    return this.http.get<Company>(this.settings.backendUrlMasterData + `/company/${id}`);
  }

  getLegalEntity(id: string): Observable<LegalEntity> {
    return this.http.get<LegalEntity>(this.settings.backendUrlMasterData + `/legalentity/${id}`);
  }

  getSimpleLegalEntityList(filter: string, companyId: string) {
    let params: HttpParams = new HttpParams();
    if (filter) params = params.set('SearchQuery', filter);
    if (companyId) params = params.set('CompanyId', companyId);
    return this.http.get<LegalEntitySimpleListItem[]>(this.settings.backendUrlMasterData + `/legalentity/all/list`, { params: params });
  }

  getJurisdictionObligation(id: string): Observable<JurisdictionObligation> {
    return this.http.get<JurisdictionObligation>(this.settings.backendUrlMasterData + `/jurisdictionobligation/${id}`);
  }

  getSimpleJurisdictionObligationList(filter: string, legalEntityId: string) {
    let params: HttpParams = new HttpParams();
    if (filter) params = params.set('SearchQuery', filter);
    if (legalEntityId) params = params.set('LegalEntityId', legalEntityId);
    return this.http.get<JurisdictionObligationSimpleListItem[]>(this.settings.backendUrlMasterData + `/jurisdictionobligation/all/list`, { params: params });
  }

  getSimpleJurisdictionObligationListByCompanyId(filter: string, companyId: string): Observable<JurisdictionObligationOption[]> {
    let params: HttpParams = new HttpParams();
    if (filter) params = params.set('SearchQuery', filter);
    if (companyId) params = params.set('CompanyId', companyId);

    return this.http.get<JurisdictionObligationOption[]>(this.settings.backendUrlMasterData + `/jurisdictionobligation/company`, { params: params })
              .pipe(map(jurisdictionObligations => jurisdictionObligations.filter(j => !this.disabledJurisdictions.includes(j.code))));
  }

  createCompany(company: Company): Observable<string> {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/company`, company); //returns created company id
  }

  createLegalEntity(legalEntity: LegalEntity): Observable<string> {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/legalentity`, legalEntity); //returns created legal entity id
  }

  createJurisdictionObligation(jurisdictionObligation: JurisdictionObligation): Observable<string> {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/jurisdictionobligation`, jurisdictionObligation); //returns created jurisdiction obligation id
  }

  updateCompany(company: Company): Observable<string> {
    return this.http.put<string>(this.settings.backendUrlMasterData + `/company/${company.id}`, company);
  }

  updateLegalEntity(legalEntity: LegalEntity): Observable<string> {
    return this.http.put<string>(this.settings.backendUrlMasterData + `/legalentity/${legalEntity.id}`, legalEntity);
  }

  updateJurisdictionObligation(jurisdictionObligation: JurisdictionObligation): Observable<string> {
    return this.http.put<string>(this.settings.backendUrlMasterData + `/jurisdictionobligation/${jurisdictionObligation.id}`, jurisdictionObligation);
  }

  deactivateCompany(companyId: string): Observable<string> {
    return this.http.delete<string>(this.settings.backendUrlMasterData + `/company/${companyId}`);
  }

  deactivateLegalEntity(legalEntityId: string): Observable<string> {
    return this.http.delete<string>(this.settings.backendUrlMasterData + `/legalentity/${legalEntityId}`);
  }

  deactivateJurisdictionObligation(jurisdictionObligationId: string): Observable<string> {
    return this.http.delete<string>(this.settings.backendUrlMasterData + `/jurisdictionobligation/${jurisdictionObligationId}`);
  }

  activateCompany(companyId: string): Observable<string> {
    return this.http.put<string>(this.settings.backendUrlMasterData + `/company/${companyId}/activate`, null);
  }

  activateLegalEntity(legalEntityId: string): Observable<string> {
    return this.http.put<string>(this.settings.backendUrlMasterData + `/legalentity/${legalEntityId}/activate`, null);
  }

  activateJurisdictionObligation(jurisdictionObligationId: string): Observable<string> {
    return this.http.put<string>(this.settings.backendUrlMasterData + `/jurisdictionobligation/${jurisdictionObligationId}/activate`, null);
  }
}